/**
 * Custom hooks for filter preset management
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { filterPresetService } from '../services';
import type { CreateFilterPresetRequest, UpdateFilterPresetRequest } from '../types/okr';

/**
 * Hook to fetch all filter presets
 */
export const useFilterPresets = () => {
  return useQuery({
    queryKey: ['filter-presets'],
    queryFn: () => filterPresetService.getPresets(),
  });
};

/**
 * Hook to fetch a single filter preset
 */
export const useFilterPreset = (id: string) => {
  return useQuery({
    queryKey: ['filter-preset', id],
    queryFn: () => filterPresetService.getPresetById(id),
    enabled: !!id,
  });
};

/**
 * Hook to fetch the default filter preset
 */
export const useDefaultFilterPreset = () => {
  return useQuery({
    queryKey: ['filter-preset', 'default'],
    queryFn: () => filterPresetService.getDefaultPreset(),
  });
};

/**
 * Hook to create a filter preset
 */
export const useCreateFilterPreset = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateFilterPresetRequest) => filterPresetService.createPreset(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['filter-presets'] });
    },
  });
};

/**
 * Hook to update a filter preset
 */
export const useUpdateFilterPreset = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateFilterPresetRequest }) =>
      filterPresetService.updatePreset(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['filter-preset', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['filter-presets'] });
    },
  });
};

/**
 * Hook to delete a filter preset
 */
export const useDeleteFilterPreset = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => filterPresetService.deletePreset(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['filter-presets'] });
    },
  });
};
